import { useEffect, useState } from "react";
import { useCampaignBuilder } from "@/context/CampaignBuilderContext";

/**
 * TEMPORARY dev panel for manually exercising the Campaign Builder draft CRUD. Saving is
 * ENTIRELY manual (no auto-save): one "Save" pushes the whole builder context to the single
 * endpoint (create if new, else PATCH). Mode is driven by the sessionStorage campaign id
 * (`campaignBuilderId`): none → CREATE (Save creates the draft); set → EDIT (Save PATCHes,
 * loaded on open). Type an id + "Open" to edit an existing campaign. Mounted in the builder
 * layout so it shows on every step (1–4). Remove when the real save UX lands.
 */
export default function DraftTestPanel() {
  const { commitAll, loadDraft, loadCampaign, deleteDraft, campaignId, mockToken, toastMessage } =
    useCampaignBuilder();
  // Input for opening an existing campaign by id; mirrors the currently-loaded id.
  const [idInput, setIdInput] = useState(campaignId);
  useEffect(() => {
    setIdInput(campaignId);
  }, [campaignId]);

  // Dev-only: rendered only when localStorage.draftCrudTest === "true". Set it in the browser
  // console (`localStorage.draftCrudTest = "true"`) and reload to exercise the manual draft CRUD.
  // Hooks run above, so this early return stays rules-of-hooks safe.
  if (typeof window !== "undefined" && localStorage.getItem("draftCrudTest") !== "true") {
    return null;
  }

  const panel: React.CSSProperties = {
    position: "fixed",
    bottom: 16,
    right: 580,
    zIndex: 10060,
    width: 240,
    background: "#fff",
    border: "2px solid #8C3B4A",
    borderRadius: 12,
    boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
    padding: 12,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  };
  const btn: React.CSSProperties = {
    padding: "8px 10px",
    borderRadius: 8,
    border: "1px solid #333",
    background: "#fff",
    color: "#333",
    font: "600 12px/1.2 Lato, sans-serif",
    textAlign: "left",
    cursor: "pointer",
  };
  const danger: React.CSSProperties = { ...btn, border: "1px solid #8C3B4A", color: "#8C3B4A" };
  const primary: React.CSSProperties = {
    ...btn,
    background: "#8C3B4A",
    color: "#fff",
    border: "1px solid #8C3B4A",
    textAlign: "center",
    font: "700 13px/1.2 Lato, sans-serif",
  };
  const input: React.CSSProperties = {
    flex: 1,
    minWidth: 0,
    padding: "7px 8px",
    borderRadius: 8,
    border: "1px solid #ccc",
    font: "600 12px/1.2 Lato, sans-serif",
    color: "#333",
  };

  return (
    <div style={panel}>
      <div style={{ font: "700 11px/1 Lato, sans-serif", color: "#8C3B4A", letterSpacing: 0.5 }}>
        TEMP · DRAFT CRUD TEST
      </div>
      <div style={{ font: "400 11px/1.3 Lato, sans-serif", color: "#5A5A5A" }}>
        {campaignId ? (
          <>
            mode: <strong>editing #{campaignId}</strong>
          </>
        ) : (
          <>
            mode: <strong>creating</strong> (no campaign yet)
          </>
        )}
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        <input
          style={input}
          value={idInput}
          onChange={(e) => setIdInput(e.target.value)}
          placeholder="campaign id"
          aria-label="Campaign id to open"
        />
        <button type="button" style={btn} onClick={() => loadCampaign(idInput)}>
          Open
        </button>
      </div>
      <button type="button" style={primary} onClick={commitAll}>
        💾 Save
      </button>
      <button type="button" style={btn} onClick={loadDraft}>
        ↺ Reload from DB
      </button>
      <button type="button" style={danger} onClick={deleteDraft}>
        ✕ Archive (soft-delete)
      </button>
      {mockToken ? (
        <a
          href={`/g/${mockToken}`}
          target="_blank"
          rel="noreferrer"
          style={{
            font: "400 11px/1.3 Lato, sans-serif",
            color: "#2B5B7B",
            wordBreak: "break-all",
          }}
        >
          Demo link → /g/{mockToken}
        </a>
      ) : null}
      {toastMessage ? (
        <div
          style={{
            font: "400 11px/1.3 Lato, sans-serif",
            color: "#3A6B4A",
            wordBreak: "break-word",
          }}
        >
          {toastMessage}
        </div>
      ) : null}
    </div>
  );
}
